import { CityState } from './types';

export class EnvironmentEngine {
  /**
   * Updates local pollution grids, distributes land value boosts, and applies environmental modifiers.
   */
  public tickEnvironment(state: CityState) {
    // 1. Reset grids
    state.pollutionGrid = {};
    state.landValueGrid = {};

    // 2. Generate and spread Industrial Pollution
    this.calculatePollution(state);

    // 3. Recalculate local Land Values across all map tiles
    this.calculateLandValues(state);
  }

  private calculatePollution(state: CityState) {
    const width = state.width;
    const height = state.height;

    // Locate completed heavy industrial sources
    const pollutionSources: { x: number; y: number; strength: number }[] = [];

    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed' && b.zone === 'industrial') {
        let strength = 12; // Low density
        if (b.density === 'medium') strength = 28;
        if (b.density === 'high') strength = 55;
        
        pollutionSources.push({ x: b.x, y: b.y, strength });
      }
    }

    // Spread pollution radially using a simple decay model
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let cellPollution = 0;

        for (const src of pollutionSources) {
          const dx = x - src.x;
          const dy = y - src.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          // Pollution falls off with distance. Max radius is ~5 cells.
          if (dist < 5.5) {
            const factor = 1.0 - dist / 5.5;
            cellPollution += src.strength * factor;
          }
        }

        const finalPollution = Math.min(100, Math.round(cellPollution));
        if (finalPollution > 0) {
          state.pollutionGrid[`${x},${y}`] = finalPollution;
        }
      }
    }
  }

  private calculateLandValues(state: CityState) {
    const width = state.width;
    const height = state.height;

    // Baseline stats of parks and services
    const landmarks: { x: number; y: number; type: 'park' | 'service'; radius: number; strength: number }[] = [];

    for (const key of Object.keys(state.buildings)) {
      const b = state.buildings[key];
      if (b.stage === 'completed') {
        if (b.zone === 'park') {
          landmarks.push({ x: b.x, y: b.y, type: 'park', radius: 4.5, strength: 40 });
        } else if (b.zone === 'civic') {
          let strength = 20;
          let radius = 4.0;
          
          if (b.name.includes('Scuola') || b.name.includes('Ospedale')) {
            strength = 35;
            radius = 5.5;
          } else if (b.name.includes('Polizia') || b.name.includes('Pompieri')) {
            strength = 25;
            radius = 5.0;
          } else if (b.name.includes('Municipio')) {
            strength = 45;
            radius = 6.0;
          }

          landmarks.push({ x: b.x, y: b.y, type: 'service', radius, strength });
        }
      }
    }

    // Update land value for each cell on the map
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const key = `${x},${y}`;
        let val = 100; // Standard base land value

        // 1. Scenic coastal beaches bonus (+25)
        const tileType = state.terrain[y]?.[x];
        if (tileType === 1) {
          val += 25; // Sandy shoreline beach
        }

        // 2. Proximity bonuses from parks and services (linear decay)
        for (const lm of landmarks) {
          const dx = x - lm.x;
          const dy = y - lm.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < lm.radius) {
            const factor = 1.0 - dist / lm.radius;
            val += lm.strength * factor;
          }
        }

        // 3. Deduct heavy local pollution
        const localPollution = state.pollutionGrid[key] || 0;
        val -= localPollution * 1.15; // Pollution crushing land values

        // 4. Deduct utility blackout penalties
        const b = state.buildings[key];
        if (b && b.stage === 'completed') {
          const isPowered = b.isPowered;
          const isWatered = b.isWatered;
          if (!isPowered || !isWatered) {
            val -= 30; // Unserviced slums suffer land value decay
          }
        }

        // Enforce bounds and write to state grid
        const finalLandValue = Math.max(20, Math.min(250, Math.round(val)));
        state.landValueGrid[key] = finalLandValue;

        // Sync with parcel values for redevelopment upgrade triggers
        const parcel = state.parcels[key];
        if (parcel) {
          parcel.landValue = finalLandValue;
        }
      }
    }
  }
}
